import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Shield, Mail, Lock } from 'lucide-react';
import TwoFactorChallenge from '../components/TwoFactorChallenge';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // Secondo fattore: { token, mode: 'mfa' | 'enroll' } quando il login richiede il codice 2FA
  const [challenge, setChallenge] = useState(null);

  // Mostra messaggio se redirect proviene da auto-logout (es. sessione scaduta)
  useEffect(() => {
    try {
      const msg = sessionStorage.getItem('jvservice_login_redirect_msg');
      if (msg) {
        toast.warning(msg, { duration: 6000 });
        sessionStorage.removeItem('jvservice_login_redirect_msg');
      }
    } catch (_) {}
    // Reset flag logout in corso (così future request 401 funzionano dopo nuovo login)
    try { window.__jvservice_logging_out = false; } catch (_) {}
  }, []);
  const { login, applySession } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const data = await login(email, password);
      if (data?.access_token) {
        toast.success('Accesso effettuato con successo');
        navigate('/dashboard');
      } else if (data?.mfa_required) {
        setChallenge({ token: data.mfa_token, mode: 'mfa' });
      } else if (data?.enrollment_required) {
        toast.message('Configura la verifica in due passaggi per continuare');
        setChallenge({ token: data.mfa_token, mode: 'enroll' });
      } else {
        toast.error('Risposta di login non valida');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'accesso');
    } finally {
      setIsLoading(false);
    }
  };

  const handle2FASuccess = (data) => {
    applySession(data);
    toast.success('Accesso effettuato con successo');
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-fade-in">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-slate-900 text-white mb-4">
            <Shield className="w-8 h-8" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            CRM-JVSERVICE
          </h1>
          <p className="text-slate-600 mt-2">GESTIONE INTELLIGENTE</p>
        </div>

        {challenge ? (
          <TwoFactorChallenge
            mfaToken={challenge.token}
            mode={challenge.mode}
            email={email}
            onSuccess={handle2FASuccess}
            onCancel={() => setChallenge(null)}
          />
        ) : (
        <Card className="border-slate-200 shadow-lg">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-semibold text-slate-900">Accedi</CardTitle>
            <CardDescription className="text-slate-500">
              Inserisci le tue credenziali per accedere
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="nome@esempio.it"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    data-testid="login-email-input"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10"
                    data-testid="login-password-input"
                    required
                  />
                </div>
              </div>
              <Button
                type="submit"
                className="w-full bg-slate-900 hover:bg-slate-800 text-white font-medium py-2.5 rounded-lg transition-all active:scale-95"
                disabled={isLoading}
                data-testid="login-submit-btn"
              >
                {isLoading ? 'Accesso in corso...' : 'Accedi'}
              </Button>
            </form>
            <div className="mt-6 text-center">
              <p className="text-sm text-slate-600">
                Non hai un account?{' '}
                <Link to="/register" className="text-slate-900 font-medium hover:underline" data-testid="register-link">
                  Registrati
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
        )}
      </div>
    </div>
  );
};

export default Login;
